<template>
    <div class="modal fade tariff-notification-modal" tabindex="-1" ref="modalElement" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" :style="{
            maxWidth: `${maxWidth + 32}px`
        }">
            <div class="modal-content tariff-notification-modal__content">
                <button type="button" class="btn-close tariff-notification-modal__close" aria-label="Закрыть"
                    :disabled="blockClose" @click="hide"></button>

                <div class="modal-body tariff-notification-modal__body">
                    <div class="row align-items-center tariff-notification-modal__row">
                        <div class="col-auto tariff-notification-modal__image-col" v-if="imageUrl">
                            <slot name="image">
                                <img v-if="imageUrl" :src="imageUrl" class="tariff-notification-modal__image"
                                    alt="Иллюстрация" />
                            </slot>
                        </div>

                        <div class="col tariff-notification-modal__content-col">
                            <h3 class="tariff-notification-modal__title pb-1 mb-2" :class="{
                                    'tariff-notification-modal__title--without-image': !imageUrl,
                                }"
                            >
                                {{ title }}
                            </h3>
                            <slot name="default"></slot>
                            <div class="tariff-notification-modal__button-wrapper">
                                <slot name="footer">
                                    <button class="btn btn-primary tariff-notification-modal__button px-4 py-1 py-md-2"
                                        @click="onButtonClick">
                                        {{ buttonText }}
                                    </button>
                                    <button
                                        v-if="secondaryButtonText"
                                        class="btn btn-outline-primary tariff-notification-modal__button tariff-notification-modal__button--secondary px-4 py-1 py-md-2"
                                        @click="onSecondaryButtonClick"
                                    >
                                        {{ secondaryButtonText }}
                                    </button>
                                </slot>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

interface Props {
    title: string;
    imageUrl?: string;
    buttonText?: string;
    secondaryButtonText?: string;
    maxWidth: number;
    /** Запретить закрытие модалки: крестиком, Escape и кликом по фону */
    blockClose?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    imageUrl: '',
    buttonText: 'Узнать подробнее',
    secondaryButtonText: '',
    maxWidth: 671,
    blockClose: false,
});

const emit = defineEmits<{
    (e: 'shown'): void;
    (e: 'hidden'): void;
    (e: 'buttonClick'): void;
    (e: 'secondaryButtonClick'): void;
}>();

const modalElement = ref<HTMLElement | null>(null);
let modalApi: typeof window.bootstrap.Modal = null;
let isProgrammaticHide = false;

const initModal = () => {
    if (!modalElement.value) return;
    modalApi = new window.bootstrap.Modal(modalElement.value, {
        backdrop: true,
        keyboard: true,
    });
    // hide.bs.modal отменяем — это разом закрывает крестик, Escape и клик по фону.
    // Владелец модалки закрывает её через hide() — такое закрытие пропускаем всегда,
    // иначе после списания окно останется висеть: prop blockClose обновится только
    // на следующем тике, а hide() зовётся в том же.
    modalElement.value.addEventListener('hide.bs.modal', (e: Event) => {
        if (isProgrammaticHide) {
            isProgrammaticHide = false;
            return;
        }

        if (props.blockClose) {
            e.preventDefault();
        }
    });
    modalElement.value.addEventListener('shown.bs.modal', () => emit('shown'));
    modalElement.value.addEventListener('hidden.bs.modal', () => emit('hidden'));
};

const show = () => {
    isProgrammaticHide = false;
    modalApi?.show();
};
const hide = () => {
    isProgrammaticHide = true;
    modalApi?.hide();
};
const onButtonClick = () => emit('buttonClick');
const onSecondaryButtonClick = () => emit('secondaryButtonClick');

onMounted(initModal);
onUnmounted(() => {
    if (modalApi && modalElement.value) modalApi.dispose?.();
});

defineExpose({ show, hide });
</script>

<style lang="scss" scoped>
.modal-dialog {
    padding: 16px;
    margin: auto;

    @media (max-width: 767.98px) {
        max-width: 320px;
    }
}

.tariff-notification-modal__row {
    gap: 16px;

    @media (max-width: 767.98px) {
        flex-direction: column;
        gap: 0;
    }
}

.tariff-notification-modal__content {
    position: relative;
    border-radius: 8px;
    background: #ffffff;
}

.tariff-notification-modal__body {
    padding: 24px;
}

.tariff-notification-modal__close {
    position: absolute;
    top: 24px;
    right: 24px;
    z-index: 1;
}

.tariff-notification-modal__image {
    width: 267px;
    height: 203px;
    object-fit: cover;
    display: block;

    @media (max-width: 767.98px) {
        width: 165px;
        height: 126px;
        margin: 0 auto 10px;
    }
}

.tariff-notification-modal__title {
    font-size: 18px;
    font-weight: 800;
    line-height: 1.4;
    margin: 0;
    max-width: calc(100% - 24px);

    @media (max-width: 767.98px) {
        font-size: 16px;
        &:not(&--without-image) {
            max-width: 100%;
        }
    }
}

.tariff-notification-modal__button-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 32px;
    text-align: left;

    @media (max-width: 576px) {
        flex-direction: column;
        align-items: stretch;
        padding-right: 32px;
        padding-left: 32px;
    }
}

.tariff-notification-modal__button {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 8px 24px;
    min-height: 34px;
    border-radius: 4px;
}

.tariff-notification-modal__button.btn-primary {
    background: #556ee6;
    border-color: #556ee6;

    &:hover,
    &:focus,
    &:active {
        background: #4458b8;
        border-color: #4458b8;
    }
}
</style>
